// CTV-682: colour derivation for printable sheets.
//
// These sheets are printed, and often in black and white. That splits into two
// separate requirements, which need different fixes:
//
//   LEGIBILITY (text vs paper) is governed by relative luminance, which is
//   essentially what a greyscale printer preserves. So a colour that clears
//   the contrast bar in colour also reads in grey — and one that doesn't,
//   doesn't, in either. Fixed by darkening the accent for text use.
//
//   DISTINCTION (one element vs another) is what greyscale actually destroys:
//   two hues can share a luminance and print as the same grey. Fixed by never
//   letting colour be the ONLY cue — weight, rules and borders carry it too.
//
// Station accents are chosen for logos, not body copy: Morgan Murphy's gold
// sits at 3.06:1 on our paper, below the 4.5:1 needed for normal text. So the
// raw accent stays for fills and borders, and `readableAccent` derives a
// darker ink for anything a reader has to actually read.

/** Sheet paper colour; both variants are close enough to share one target. */
export const SHEET_PAPER = '#faf9f7';
/** Body-copy ink, used to check the accent doesn't collapse into it in grey. */
export const SHEET_INK = '#0f172a';
/** WCAG AA for normal-size text. */
const AA_NORMAL = 4.5;

export function parseHex(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex([r, g, b]: [number, number, number]): string {
  const p = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${p(r)}${p(g)}${p(b)}`;
}

/** WCAG relative luminance. Also a good proxy for how a colour prints in grey. */
export function relativeLuminance(hex: string): number {
  const rgb = parseHex(hex);
  if (!rgb) return 0;
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two colours, 1:1 (identical) to 21:1. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * The accent darkened just far enough to clear AA for normal text against the
 * sheet paper. Returns the accent untouched when it already passes, so a
 * station whose brand colour is already dark keeps it exactly.
 *
 * Darkening (rather than picking an unrelated colour) keeps the hue, so the
 * sheet still reads as the station's colour in a colour print while staying
 * legible in grey.
 */
export function readableAccent(hex: string, paper: string = SHEET_PAPER): string {
  const rgb = parseHex(hex);
  if (!rgb) return SHEET_INK;
  if (contrastRatio(hex, paper) >= AA_NORMAL) return hex;

  let [r, g, b] = rgb;
  // Multiplicative steps preserve hue far better than subtracting a constant,
  // which drags colours toward whichever channel bottoms out first.
  for (let i = 0; i < 40; i += 1) {
    r *= 0.92;
    g *= 0.92;
    b *= 0.92;
    const candidate = toHex([r, g, b]);
    if (contrastRatio(candidate, paper) >= AA_NORMAL) return candidate;
  }
  return SHEET_INK;
}

/** `#rrggbb` -> `rgba(...)`, for the washes and hairlines that track the accent. */
export function accentAlpha(hex: string, alpha: number): string {
  const rgb = parseHex(hex);
  if (!rgb) return `rgba(15, 23, 42, ${alpha})`;
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
}

// ---------------------------------------------------------------------------
// Shade ladder
//
// One tone everywhere made the sheet read flat — the pill, both cards and the
// CTA panel were the same colour. The design it came from used two: dark
// cards against a lighter panel. So the sheet derives a ladder from a single
// brand hue rather than mixing two brand colours, which keeps every station
// harmonious (several have genuinely clashing primary/accent pairs — navy and
// red, blue and orange).
// ---------------------------------------------------------------------------

function hexToHsl(hex: string): [number, number, number] | null {
  const rgb = parseHex(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb.map((v) => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const hue =
    max === r ? ((g - b) / d + (g < b ? 6 : 0)) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [hue / 6, s, l];
}

function hslToHex(hue: number, s: number, l: number): string {
  const f = (n: number) => {
    const k = (n + hue * 12) % 12;
    const a = s * Math.min(l, 1 - l);
    const v = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(v * 255).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

/** Same hue, set to a target lightness. Grey brands get a saturation floor so
 *  the ladder still reads as a colour rather than three greys. */
function shadeAt(hex: string, lightness: number): string {
  const hsl = hexToHsl(hex);
  if (!hsl) return SHEET_INK;
  const [hue, s] = hsl;
  return hslToHex(hue, Math.max(s, 0.15), lightness);
}

/** WCAG AA for large or bold display text, the bar a banner's button clears. */
const AA_LARGE = 3;

/** Darken until white text on this fill clears `ratio` (AA for normal text by default). */
export function holdsWhiteText(hex: string, ratio: number = AA_NORMAL): string {
  let candidate = hex;
  for (let i = 0; i < 30 && contrastRatio(candidate, '#ffffff') < ratio; i += 1) {
    const hsl = hexToHsl(candidate);
    if (!hsl) return SHEET_INK;
    candidate = hslToHex(hsl[0], hsl[1], Math.max(0, hsl[2] - 0.03));
  }
  return candidate;
}

/**
 * Which brand colour to build the ladder from. Skips anything near-white or
 * near-black: a white primary would vanish, and a black accent would give a
 * station four solid black blocks — a lot of toner on a sheet meant to be
 * printed.
 */
export function ladderSource(accent?: string | null, primary?: string | null): string {
  for (const candidate of [accent, primary]) {
    if (!candidate) continue;
    const hsl = hexToHsl(candidate);
    if (hsl && hsl[2] >= 0.12 && hsl[2] <= 0.72) return candidate;
  }
  return accent || primary || SHEET_INK;
}

export interface SheetPalette {
  /** As configured. Fills and hairlines where contrast doesn't matter. */
  accent: string;
  /** AA on paper. Every accent-coloured piece of TEXT. */
  ink: string;
  /** Darkest rung: the budget pill and the reversed cards. */
  deep: string;
  /** Middle rung: the CTA panel and the TV screen. */
  mid: string;
  /**
   * Lightest rung. A solid tint, not an alpha wash, so it reads as its own
   * step against the paper — the step cards were plain white on off-white,
   * which is barely a distinction at all.
   */
  soft: string;
  wash: string;
  edge: string;
}

export function sheetPalette(config: {
  accentColor?: string | null;
  primaryColor?: string | null;
}): SheetPalette {
  const accent = config.accentColor || config.primaryColor || SHEET_INK;
  const source = ladderSource(config.accentColor, config.primaryColor);
  return {
    accent,
    ink: readableAccent(accent),
    deep: holdsWhiteText(shadeAt(source, 0.22)),
    mid: holdsWhiteText(shadeAt(source, 0.42)),
    soft: shadeAt(source, 0.94),
    wash: accentAlpha(source, 0.08),
    edge: accentAlpha(source, 0.35),
  };
}

// ---------------------------------------------------------------------------
// Dark banner palette (CTV-840)
//
// Display ads sit on a station's website, not on paper, and the design they
// are built from is white type on a deep field of the station's own hue. The
// ladder above is the wrong shape for that: its rungs are fills on off-white
// paper. This one derives what a dark banner needs from the same source hue,
// so the ads and the sheets read as one brand.
// ---------------------------------------------------------------------------

/** Lighten until this colour, used as TEXT on `bg`, clears AA. */
function holdsOnDark(hex: string, bg: string): string {
  let candidate = hex;
  for (let i = 0; i < 30 && contrastRatio(candidate, bg) < AA_NORMAL; i += 1) {
    const hsl = hexToHsl(candidate);
    if (!hsl) return '#ffffff';
    candidate = hslToHex(hsl[0], hsl[1], Math.min(1, hsl[2] + 0.03));
  }
  return candidate;
}

/** Dark is the design as drawn; light (feedback 2026-09-03) lets a station whose logo only works on white use it as drawn. */
export type BannerLook = 'dark' | 'light';

export interface BannerPalette {
  /** Top of the background gradient. Contrast is checked against this end. */
  bgTop: string;
  /** Bottom of the background gradient, a step away from the top. */
  bgBottom: string;
  /** Body and headline text. White on dark; near-black on light. */
  ink: string;
  /**
   * The call-to-action fill. Holds white text at the large-text bar, not the
   * body-text one: the label is a short bold caps line, and darkening a
   * light brand blue to 4.5:1 turned the reference's bright button teal.
   */
  button: string;
  /** The tinted words in the headline and the tagline. AA as text on `bgTop`. */
  highlight: string;
  /** The TV screen. A step up from the field so the set reads as switched on; holds white text. */
  screen: string;
  /** The far end of the screen's gradient, deeper than `screen`; holds white text in both looks. */
  screenDeep: string;
}

export function bannerPalette(
  config: { accentColor?: string | null; primaryColor?: string | null },
  look: BannerLook = 'dark',
): BannerPalette {
  const source = ladderSource(config.accentColor, config.primaryColor);
  // The TV screen is the brand's mid tone in both looks: a lit screen on a
  // pale field is what keeps the light banner from reading as a print sheet.
  const screen = holdsWhiteText(shadeAt(source, 0.3));
  const screenDeep = holdsWhiteText(shadeAt(source, 0.16));
  if (look === 'light') {
    const bgTop = shadeAt(source, 0.97);
    return {
      bgTop,
      bgBottom: shadeAt(source, 0.92),
      ink: SHEET_INK,
      button: holdsWhiteText(shadeAt(source, 0.45), AA_LARGE),
      // Darkened until it reads as text on the pale field, same rule as the
      // print sheets' readableAccent.
      highlight: readableAccent(shadeAt(source, 0.45), bgTop),
      screen,
      screenDeep,
    };
  }
  const bgTop = shadeAt(source, 0.14);
  return {
    bgTop,
    bgBottom: shadeAt(source, 0.07),
    ink: '#ffffff',
    button: holdsWhiteText(shadeAt(source, 0.55), AA_LARGE),
    highlight: holdsOnDark(shadeAt(source, 0.62), bgTop),
    screen,
    screenDeep,
  };
}
